import React, { useCallback, useEffect, useRef, useState } from "react";
import { authFetch } from "../../HttpClient";

export const PAGE_SIZE = 25;

// Formatte une durée en secondes en libellé court ("45s", "12 min", "1h30").
function formatDuration(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return remMinutes > 0 ? `${hours}h${remMinutes}` : `${hours}h`;
}

// Le filtre de date (aujourd'hui/hier/cette semaine/ce mois) est géré
// globalement par SuperAdmin.js et transmis en props à chaque onglet.
// `initialData` ({ total, items }) est la page 1 déjà préchargée par
// SuperAdmin.js pendant que la vue d'ensemble était affichée : on l'utilise
// comme état de départ pour que le tableau s'affiche instantanément à
// l'ouverture, sans re-fetch ni skeleton — seuls les changements de page ou
// de filtre déclenchent un nouveau chargement (avec son propre skeleton).
function ConnexionsTab({ from, to, initialData }) {
  const [items, setItems] = useState(initialData?.items || []);
  const [total, setTotal] = useState(initialData?.total || 0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(!initialData);
  const skipNextFetch = useRef(Boolean(initialData));

  // Revient à la page 1 à chaque changement de préréglage de date.
  useEffect(() => {
    setPage(1);
  }, [from, to]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
      });
      if (from) params.set("from", from);
      if (to) params.set("to", to);

      const response = await authFetch(
        `https://backend-myalfa.vercel.app/api/super-admin/login-logs?${params.toString()}`
      );
      const data = await response.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Erreur lors de la récupération des connexions :", error);
    } finally {
      setLoading(false);
    }
  }, [page, from, to]);

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    fetchLogs();
  }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-base font-semibold text-gray-900">Connexions</h2>
        <span className="text-xs font-semibold bg-gray-100 text-gray-500 rounded-full px-2.5 py-1">
          {total}
        </span>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <table className="w-full" style={{ fontSize: "0.72rem" }}>
          <thead>
            <tr className="bg-[#6e1a36] text-xs">
              <th className="px-3 py-2.5 text-white font-semibold text-left">
                Collaborateur
              </th>
              <th className="px-3 py-2.5 text-white font-semibold text-left">
                Email
              </th>
              <th className="px-3 py-2.5 text-white font-semibold text-left">
                Date &amp; heure de connexion
              </th>
              <th className="px-3 py-2.5 text-white font-semibold text-left">
                Répartition par écran
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-3 py-3" colSpan={4}>
                    <div className="h-3 bg-gray-100 rounded animate-pulse" />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-8 text-center text-gray-400"
                >
                  Aucune connexion enregistrée sur cette période.
                </td>
              </tr>
            ) : (
              items.map((log) => (
                <tr key={log.id}>
                  <td className="px-3 py-2.5 font-medium text-slate-900">
                    {log.user?.name} {log.user?.lastName}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">
                    {log.user?.email}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">
                    {new Date(log.createdAt).toLocaleString("fr-FR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">
                    {log.sectionTimes && log.sectionTimes.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {[...log.sectionTimes]
                          .sort((a, b) => b.seconds - a.seconds)
                          .map((s) => (
                            <span
                              key={s.view}
                              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600"
                            >
                              {s.view} · {formatDuration(s.seconds)}
                            </span>
                          ))}
                      </div>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
          >
            Précédent
          </button>
          <span className="text-xs text-gray-400">
            Page {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  );
}

export default ConnexionsTab;
